<template>
  <div 
    class="file-preview-card relative inline-block p-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-sm hover:shadow-md transition-all duration-200 cursor-pointer group"
    @click="handleFileClick"
    v-tooltip.top="tooltipDisplay"
  >
    <!-- Close button for removable files -->
    <button
      v-if="removable"
      type="button"
      @click.stop="handleRemove"
      class="absolute -top-1 -right-1 w-5 h-5 z-30 bg-red-500 hover:bg-red-600 text-white rounded-full flex items-center justify-center text-xs opacity-0 group-hover:opacity-100 transition-opacity duration-200"
    >
      <i class="pi pi-times z-30"></i>
    </button>

    <!-- Image/video preview or file icon -->
    <div class="flex flex-col items-center">
      <div class="relative w-16 h-16 mb-1">
        <!-- Image preview -->
        <img 
          v-if="isImage && !imageError && (imageUrl || fileUrl)"
          :src="imageUrl || fileUrl"
          :alt="fileName"
          class="w-full h-full object-cover rounded border"
          @error="onImageError"
          @load="onImageLoad"
        />
        <!-- Video preview -->
        <video 
          v-else-if="isVideo && !videoError && (imageUrl || fileUrl)"
          :src="imageUrl || fileUrl"
          class="w-full h-full object-cover rounded border"
          muted
          preload="metadata"
          @error="onVideoError"
          @loadedmetadata="onVideoLoad"
        >
          <i 
            :class="fileIconClass" 
            class="text-2xl"
            :style="{ color: fileIconColor }"
          ></i>
        </video>
        <!-- File icon for non-media files or failed loads -->
        <div 
          v-else
          class="w-full h-full flex items-center justify-center bg-gray-100 dark:bg-gray-700 rounded border"
        >
          <i 
            :class="fileIconClass" 
            class="text-2xl"
            :style="{ color: fileIconColor }"
          ></i>
        </div>
      </div>
      
      <!-- File name -->
      <div class="text-center w-20">
        <p class="text-xs font-medium text-gray-900 dark:text-gray-100 truncate">
          {{ fileName }}
        </p>
        <p class="text-xs text-gray-500 dark:text-gray-400">
          {{ fileSize }}
        </p>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, ref, watch, onUnmounted } from 'vue';

const props = defineProps({
  fileName: {
    type: String,
    required: true
  },
  fileSize: {
    type: String,
    default: ''
  },
  mimeType: {
    type: String,
    default: ''
  },
  removable: {
    type: Boolean,
    default: false
  },
  file: {
    type: File,
    default: null
  },
  fileUrl: {
    type: String,
    default: null
  },
  /** When set, used for hover tooltip instead of fileName (avoids duplicate v-tooltip on parent). */
  tooltip: {
    type: String,
    default: null
  }
});

const tooltipDisplay = computed(() => props.tooltip || props.fileName);

const emit = defineEmits(['click', 'remove']);

const imageUrl = ref(null);
const imageError = ref(false);
const videoError = ref(false);

// Check if file is an image
const isImage = computed(() => {
  if (!props.fileName && !props.mimeType) return false;
  
  const ext = props.fileName?.toLowerCase().split('.').pop() || '';
  const mime = props.mimeType?.toLowerCase() || '';
  
  const isImageExt = ext.match(/^(jpg|jpeg|png|gif|svg|webp|bmp|tiff)$/i);
  const isImageMime = mime.startsWith('image/');
  
  const result = isImageExt || isImageMime;
  
  
  return result;
});

// Check if file is a video
const isVideo = computed(() => {
  if (!props.fileName && !props.mimeType) return false;
  
  const ext = props.fileName?.toLowerCase().split('.').pop() || '';
  const mime = props.mimeType?.toLowerCase() || '';
  
  const isVideoExt = ext.match(/^(mp4|avi|mov|wmv|flv|mkv|webm)$/i);
  const isVideoMime = mime.startsWith('video/');
  
  return isVideoExt || isVideoMime;
});

// Extract file extension
const fileExtension = computed(() => {
  if (!props.fileName) return '';
  const parts = props.fileName.split('.');
  return parts.length > 1 ? parts[parts.length - 1] : '';
});

// Determine file icon and color based on file type
const fileIconClass = computed(() => {
  const ext = fileExtension.value.toLowerCase();
  const mime = props.mimeType?.toLowerCase() || '';

  // PDF
  if (ext === 'pdf' || mime.includes('pdf')) {
    return 'pi pi-file-pdf';
  }
  
  // Word documents
  if (ext.match(/\.(doc|docx)$/i) || mime.includes('word') || mime.includes('document')) {
    return 'pi pi-file-word';
  }
  
  // Excel files
  if (ext.match(/\.(xls|xlsx)$/i) || mime.includes('excel') || mime.includes('spreadsheet')) {
    return 'pi pi-file-excel';
  }
  
  // PowerPoint
  if (ext.match(/\.(ppt|pptx)$/i) || mime.includes('powerpoint') || mime.includes('presentation')) {
    return 'pi pi-file-powerpoint';
  }
  
  // Videos
  if (ext.match(/\.(mp4|avi|mov|wmv|flv|mkv|webm)$/i) || mime.startsWith('video/')) {
    return 'pi pi-file-video';
  }
  
  // Audio
  if (ext.match(/\.(mp3|wav|ogg|flac|aac|m4a)$/i) || mime.startsWith('audio/')) {
    return 'pi pi-file-audio';
  }
  
  // Archives
  if (ext.match(/\.(zip|rar|7z|tar|gz|bz2)$/i) || mime.includes('archive') || mime.includes('zip')) {
    return 'pi pi-file-archive';
  }
  
  // Text files
  if (ext.match(/\.(txt|log|md|json|xml|csv)$/i) || mime.startsWith('text/')) {
    return 'pi pi-file';
  }
  
  // Default file icon
  return 'pi pi-file';
});

// Determine file icon color
const fileIconColor = computed(() => {
  const ext = fileExtension.value.toLowerCase();
  const mime = props.mimeType?.toLowerCase() || '';

  // PDF - red
  if (ext === 'pdf' || mime.includes('pdf')) {
    return '#dc2626';
  }
  
  // Word - blue
  if (ext.match(/\.(doc|docx)$/i) || mime.includes('word') || mime.includes('document')) {
    return '#2563eb';
  }
  
  // Excel - green
  if (ext.match(/\.(xls|xlsx)$/i) || mime.includes('excel') || mime.includes('spreadsheet')) {
    return '#16a34a';
  }
  
  // PowerPoint - orange
  if (ext.match(/\.(ppt|pptx)$/i) || mime.includes('powerpoint') || mime.includes('presentation')) {
    return '#ea580c';
  }
  
  // Videos - purple
  if (ext.match(/\.(mp4|avi|mov|wmv|flv|mkv|webm)$/i) || mime.startsWith('video/')) {
    return '#9333ea';
  }
  
  // Audio - pink
  if (ext.match(/\.(mp3|wav|ogg|flac|aac|m4a)$/i) || mime.startsWith('audio/')) {
    return '#ec4899';
  }
  
  // Archives - yellow
  if (ext.match(/\.(zip|rar|7z|tar|gz|bz2)$/i) || mime.includes('archive') || mime.includes('zip')) {
    return '#eab308';
  }
  
  // Default - gray
  return '#6b7280';
});

// Create media preview URL when file changes
watch(() => props.file, (newFile) => {
  if (newFile && (isImage.value || isVideo.value)) {
    // Clean up previous URL
    if (imageUrl.value) {
      URL.revokeObjectURL(imageUrl.value);
    }
    
    // Create new URL for media preview
    imageUrl.value = URL.createObjectURL(newFile);
    imageError.value = false;
    videoError.value = false;
  }
}, { immediate: true });

// Watch for fileUrl changes and handle CORS issues for remote media
watch(() => props.fileUrl, async (newUrl) => {
  if (newUrl && (isImage.value || isVideo.value) && !props.file) {
    imageError.value = false;
    videoError.value = false;
    
    // Try to load media as blob to avoid CORS issues
    try {
      const response = await fetch(newUrl);
      if (response.ok) {
        const blob = await response.blob();
        const blobUrl = URL.createObjectURL(blob);
        
        // Clean up previous URL
        if (imageUrl.value) {
          URL.revokeObjectURL(imageUrl.value);
        }
        
        imageUrl.value = blobUrl;
      } else {
        console.warn(`Failed to fetch media: ${response.status} ${response.statusText}`);
        imageError.value = true;
        videoError.value = true;
      }
    } catch (error) {
      console.warn(`Error loading media as blob: ${error.message}`);
      imageError.value = true;
      videoError.value = true;
    }
  }
}, { immediate: true });

// Clean up URL on unmount
const cleanup = () => {
  if (imageUrl.value) {
    URL.revokeObjectURL(imageUrl.value);
  }
};

// Handle image load success
const onImageLoad = () => {
  imageError.value = false;
};

// Handle image load error
const onImageError = (event) => {
  imageError.value = true;
  if (imageUrl.value) {
    URL.revokeObjectURL(imageUrl.value);
    imageUrl.value = null;
  }
  // Log error for debugging
  console.warn(`Failed to load image: ${props.fileName}`, {
    fileUrl: props.fileUrl,
    mimeType: props.mimeType,
    error: event,
    src: event.target?.src
  });
};

// Handle video load success
const onVideoLoad = () => {
  videoError.value = false;
};

// Handle video load error
const onVideoError = (event) => {
  videoError.value = true;
  if (imageUrl.value) {
    URL.revokeObjectURL(imageUrl.value);
    imageUrl.value = null;
  }
  // Log error for debugging
  console.warn(`Failed to load video: ${props.fileName}`, {
    fileUrl: props.fileUrl,
    mimeType: props.mimeType,
    error: event,
    src: event.target?.src
  });
};

const handleFileClick = () => {
  emit('click');
};

const handleRemove = () => {
  cleanup();
  emit('remove');
};

// Cleanup on unmount
onUnmounted(() => {
  cleanup();
});
</script>

<style scoped>
.file-preview-card {
  width: 100px;
  min-width: 100px;
  max-width: 100px;
}

.file-preview-card:hover {
  transform: translateY(-1px);
}
</style>

